import React, { useEffect, useState, useCallback } from "react";
import {
  Box,
  Typography,
  List,
  ListItem,
  CircularProgress,
  Button,
  styled,
  useMediaQuery,
} from "@mui/material";
import { getAuth, onAuthStateChanged } from "firebase/auth"; // Firebase Auth
import Navigation from "../../../navigations/Navigation";
import HistoryTab from "../../../components/HistoryTab";
import { CallApiWithToken } from "../../../api/ApiRequest";
import { api_getReferral } from "../../../api/APIEndpoint";
import { getDisplayName } from "../../../utils/Utils";
import Loader from "../../../components/Loader";
import Alert from "../../../components/Alert";

function EarningScreen({ className }) {
  const [user, setUser] = useState(null);
  const [referral, setReferral] = useState([]);
  const [isLoading, setIsLoading] = useState(false);
  const [refreshing, setRefreshing] = useState(false);
  const [alert, setAlert] = useState({
    open: false,
    message: "",
    severity: "error",
  });

  const isMobile = useMediaQuery("(max-width: 600px)");
  const isTablet = useMediaQuery("(max-width: 900px)");

  // Get the current user from Firebase
  useEffect(() => {
    const auth = getAuth();
    const unsubscribe = onAuthStateChanged(auth, (firebaseUser) => {
      setUser(firebaseUser || null);
    });
    return () => unsubscribe();
  }, []);

  // Fetch referral data
  const getReferrals = async () => {
    if (!user) return;

    setIsLoading(true);
    try {
      const res = await CallApiWithToken("POST", `${api_getReferral}`, {
        FullName: getDisplayName(user.displayName),
        PhoneNumber: user.phoneNumber?.slice(2) || "",
        Email: user.email,
      });

      const parsedResponse = typeof res === "string" ? JSON.parse(res) : res;
      if (parsedResponse?.Referrals) {
        setReferral(parsedResponse.Referrals);
      } else {
        console.error("Unexpected response structure:", parsedResponse);
      }
    } catch (error) {
      console.error("API Error:", error);
      if (error.response) {
        setAlert({
          open: true,
          message: `An error occurred: ${
            error.response.statusText || "Unknown error"
          }`,
          severity: "error",
        });
      } else if (error.request) {
        setAlert({
          open: true,
          message:
            "Unable to reach the server. Please check your internet connection.",
          severity: "error",
        });
      } else {
        setAlert({
          open: true,
          message: "Something went wrong. Please try again later.",
          severity: "error",
        });
      }
    } finally {
      setIsLoading(false);
    }
  };

  useEffect(() => {
    if (user) getReferrals();
  }, [user]);

  // Refresh functionality
  const onRefresh = useCallback(() => {
    setRefreshing(true);
    getReferrals().finally(() => setRefreshing(false));
  }, [user]);

  // Calculate total referral amount
  const totalAmount = referral.reduce(
    (sum, ref) => sum + parseFloat(ref.ReferralAmount || 0),
    0
  );

  return (
    <>
      <Navigation />
      <div className={className}>
        <Box className="scrollableContent">
          <Box className="container">
            <Typography className="text">Earning</Typography>
            <Box className="totalPoints">
              <Typography className="subHeading">Current Points</Typography>
              <Typography className="points">{totalAmount}</Typography>
            </Box>

            {referral.length > 0 && (
              <Typography className="heading">History</Typography>
            )}

            {isLoading ? (
              <Loader loading={isLoading} />
            ) : referral.length > 0 ? (
              <List className="historyList">
                {referral.map((item, index) => (
                  <ListItem key={index} className="earning-box">
                    <HistoryTab item={item} />
                  </ListItem>
                ))}
              </List>
            ) : (
              <Typography className="noHistory">
                No referral history available.
              </Typography>
            )}
          </Box>
        </Box>
      </div>
      <Alert
        open={alert.open}
        message={alert.message}
        severity={alert.severity}
        onClose={() => setAlert({ ...alert, open: false })}
      />
    </>
  );
}

export default styled(EarningScreen)(({}) => ({
  "*": {
    margin: 0,
    padding: 0,
    boxSizing: "border-box",
  },
  "html, body, #root": {
    height: "100%",
    overflow: "hidden",
  },
  ".scrollableContent": {
    position: "fixed",
    top: "64px", // Adjust based on header height
    bottom: "56px", // Adjust based on footer height
    left: 0,
    right: 0,
    overflowY: "auto",
    padding: "20px",
    display: "flex",
    flexDirection: "column",
    alignItems: "center",
  },
  ".container": {
    width: "100%",
    maxWidth: "1200px",
    display: "flex",
    flexDirection: "column",
    alignItems: "center",
  },
  ".text": {
    fontWeight: 700,
    fontSize: "60px",
    color: "#234000",
    textAlign: "center",
    marginBottom: "20px",
    "@media (max-width: 900px)": {
      fontSize: "45px",
    },
    "@media (max-width: 768px)": {
      display: "none",
    },
  },
  ".totalPoints": {
    backgroundColor: "#DCEBBA",
    borderRadius: "10px",
    width: "80%",
    maxWidth: "606px",
    height: "140px",
    display: "flex",
    flexDirection: "column",
    justifyContent: "center",
    alignItems: "center",
    marginTop: "22px",
    "@media (max-width: 600px)": {
      height: "100px",
    },
  },
  ".subHeading": {
    fontSize: "30px",
    "@media (max-width: 600px)": {
      fontSize: "20px",
    },
  },
  ".points": {
    fontSize: "60px",
    "@media (max-width: 600px)": {
      fontSize: "40px",
    },
  },
  ".heading": {
    fontSize: "32px",
    fontWeight: 600,
    marginTop: "12px",
    "@media (max-width: 600px)": {
      fontSize: "24px",
    },
  },
  ".historyList": {
    width: "100%",
    maxWidth: "606px",
    padding: 0,
    "@media (max-width: 600px)": {
      width: "100%",
    },
  },
  ".earning-box": {
    width: "100%",
  },
  ".noHistory": {
    fontSize: "22px",
    textAlign: "center",
    fontFamily: "LeagueSpartan-SemiBold",
    marginTop: "30px",
    marginBottom: "20px",
  },
}));
